(function () {
  var FORMS = {
    AGLContactForm: 'contact',
    AGLRmaForm: 'rma',
    ServicesProjectForm: 'services',
  };
  var CONFIG_ENDPOINT = '/apps/quotes/forms/config';
  var PREPARE_ENDPOINT = '/apps/quotes/forms/prepare';
  var CONFIRM_ENDPOINT = '/apps/quotes/forms/confirmation';
  var TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var STORAGE_PREFIX = 'agl:native-form-confirmation:';
  var MAX_AGE_MS = 30 * 60 * 1000;
  var MAX_RECONCILIATION_ATTEMPTS = 3;
  var RECONCILIATION_DELAY_MS = 2000;
  var PREPARE_TIMEOUT_MS = 12000;
  var PREPARE_BUDGET_MS = 8000;
  var nativeSubmitReady = new WeakSet();
  var preparingForms = new WeakSet();
  var configPromise;
  var turnstileScriptPromise;

  function storageKey(kind) {
    return STORAGE_PREFIX + kind;
  }

  function clearStoredConfirmation(kind) {
    try { sessionStorage.removeItem(storageKey(kind)); } catch (_ignored) {}
  }

  function createIdempotencyKey() {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    var bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    var hex = Array.prototype.map.call(bytes, function (byte) {
      return byte.toString(16).padStart(2, '0');
    }).join('');
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join('-');
  }

  function locale() {
    return /^en(?:-|$)/i.test(document.documentElement.lang || '') ? 'en' : 'pt-PT';
  }

  function readRmaDetails(form, kind) {
    if (kind !== 'rma') return null;
    var orderInput = form.querySelector(
      'input[name="contact[Número da encomenda ou fatura]"]',
    );
    var productInput = form.querySelector(
      'input[name="contact[Produto ou referência]"]',
    );
    var serialInput = form.querySelector(
      'input[name="contact[Número de série]"]',
    );
    var orderReference = orderInput && orderInput.value.trim();
    var productReference = productInput && productInput.value.trim();
    var serialNumber = serialInput ? serialInput.value.trim() : '';
    if (
      !orderReference ||
      orderReference.length > 100 ||
      !productReference ||
      productReference.length > 100 ||
      serialNumber.length > 40
    ) {
      throw new Error('RMA history details unavailable');
    }
    return {
      orderReference: orderReference,
      productReference: productReference,
      serialNumber: serialNumber,
    };
  }

  function stageConfirmationKey(kind, idempotencyKey, envelope) {
    var pending = {
      idempotencyKey: idempotencyKey,
      envelope: envelope,
      createdAt: Date.now(),
      completed: false,
    };
    try {
      sessionStorage.setItem(storageKey(kind), JSON.stringify(pending));
    } catch (_error) {
      // Confirmation is supplemental; never block Shopify's native POST.
    }
  }

  function readConfirmationKey(kind) {
    var raw;
    try {
      raw = sessionStorage.getItem(storageKey(kind));
      if (!raw) return null;
      var pending = JSON.parse(raw);
      if (
        typeof pending.idempotencyKey !== 'string' ||
        typeof pending.envelope !== 'string' ||
        typeof pending.createdAt !== 'number' ||
        typeof pending.completed !== 'boolean' ||
        Date.now() - pending.createdAt > MAX_AGE_MS
      ) {
        sessionStorage.removeItem(storageKey(kind));
        return null;
      }
      return pending;
    } catch (_error) {
      try {
        sessionStorage.removeItem(storageKey(kind));
      } catch (_ignored) {}
      return null;
    }
  }

  function fetchWithTimeout(url, options, externalSignal) {
    var controller = new AbortController();
    var timeout = setTimeout(function () { controller.abort(); }, PREPARE_TIMEOUT_MS);
    function abortFromExternal() { controller.abort(); }
    if (externalSignal) {
      if (externalSignal.aborted) controller.abort();
      else externalSignal.addEventListener('abort', abortFromExternal, { once: true });
    }
    var requestOptions = Object.assign({}, options, { signal: controller.signal });
    return fetch(url, requestOptions).finally(function () {
      clearTimeout(timeout);
      if (externalSignal) externalSignal.removeEventListener('abort', abortFromExternal);
    });
  }

  function loadConfig(signal) {
    if (!configPromise) {
      configPromise = fetchWithTimeout(CONFIG_ENDPOINT, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }, signal).then(function (response) {
        if (!response.ok) throw new Error('configuration unavailable');
        return response.json();
      }).then(function (config) {
        if (!config || typeof config.turnstileSiteKey !== 'string') {
          throw new Error('invalid configuration');
        }
        return config.turnstileSiteKey;
      }).catch(function (error) {
        configPromise = undefined;
        throw error;
      });
    }
    return configPromise;
  }

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!turnstileScriptPromise) {
      turnstileScriptPromise = new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        var settled = false;
        var timeout = setTimeout(function () {
          if (settled) return;
          settled = true;
          script.remove();
          reject(new Error('Turnstile unavailable'));
        }, PREPARE_TIMEOUT_MS);
        script.src = TURNSTILE_SCRIPT;
        script.async = true;
        script.defer = true;
        script.onload = function () {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          if (window.turnstile) resolve(window.turnstile);
          else {
            script.remove();
            reject(new Error('Turnstile unavailable'));
          }
        };
        script.onerror = function () {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          script.remove();
          reject(new Error('Turnstile unavailable'));
        };
        document.head.appendChild(script);
      }).catch(function (error) {
        turnstileScriptPromise = undefined;
        throw error;
      });
    }
    return turnstileScriptPromise;
  }

  function requestTurnstileToken(form, siteKey, signal) {
    if (signal && signal.aborted) return Promise.reject(new Error('verification aborted'));
    return loadTurnstile().then(function (turnstile) {
      return new Promise(function (resolve, reject) {
        var container = document.createElement('div');
        container.className = 'agl-native-form-turnstile';
        form.appendChild(container);
        var settled = false;
        var widgetId;
        var timeout = setTimeout(function () {
          finish(new Error('verification timeout'));
        }, PREPARE_TIMEOUT_MS);
        function abortVerification() {
          finish(new Error('verification aborted'));
        }
        if (signal) signal.addEventListener('abort', abortVerification, { once: true });
        function finish(error, token) {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          if (signal) signal.removeEventListener('abort', abortVerification);
          if (typeof widgetId !== 'undefined') {
            try { turnstile.remove(widgetId); } catch (_ignored) {}
          }
          try { container.remove(); } catch (_ignored) {}
          if (error) reject(error);
          else resolve(token);
        }
        try {
          widgetId = turnstile.render(container, {
            sitekey: siteKey,
            action: 'native_form_confirmation',
            execution: 'execute',
            appearance: 'interaction-only',
            callback: function (token) { finish(null, token); },
            'error-callback': function () { finish(new Error('verification failed')); },
            'expired-callback': function () { finish(new Error('verification expired')); },
            'timeout-callback': function () { finish(new Error('verification timeout')); },
          });
          turnstile.execute(widgetId);
        } catch (error) {
          finish(error);
        }
      });
    });
  }

  function prepareConfirmation(form, kind, signal) {
    var emailInput = form.querySelector('input[name="contact[email]"]');
    if (!emailInput || !emailInput.value) return Promise.reject(new Error('email unavailable'));
    var preparedEmail = emailInput.value;
    var preparedRmaDetails;
    try {
      preparedRmaDetails = readRmaDetails(form, kind);
    } catch (error) {
      return Promise.reject(error);
    }
    var idempotencyKey = createIdempotencyKey();
    return loadConfig(signal).then(function (siteKey) {
      return requestTurnstileToken(form, siteKey, signal);
    }).then(function (turnstileToken) {
      return fetchWithTimeout(
        PREPARE_ENDPOINT + '?idempotency_key=' + encodeURIComponent(idempotencyKey),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(Object.assign({
            email: preparedEmail,
            formKind: kind,
            locale: locale(),
            turnstileToken: turnstileToken,
          }, preparedRmaDetails ? { rmaDetails: preparedRmaDetails } : {})),
          credentials: 'same-origin',
        },
        signal,
      );
    }).then(function (response) {
      if (response.status !== 201) throw new Error('prepare rejected');
      return response.json();
    }).then(function (result) {
      if (!result || typeof result.envelope !== 'string') {
        throw new Error('invalid envelope');
      }
      if (emailInput.value !== preparedEmail) {
        throw new Error('email changed during preparation');
      }
      stageConfirmationKey(kind, idempotencyKey, result.envelope);
    });
  }

  function prepareWithinBudget(form, kind) {
    var controller = new AbortController();
    var timeout = setTimeout(function () { controller.abort(); }, PREPARE_BUDGET_MS);
    return prepareConfirmation(form, kind, controller.signal).finally(function () {
      clearTimeout(timeout);
    });
  }

  function resumeNativeSubmit(form, submitter) {
    function clearNativeSubmitReady() {
      nativeSubmitReady.delete(form);
    }
    nativeSubmitReady.add(form);
    try {
      form.requestSubmit(submitter || undefined);
    } catch (_error) {
      nativeSubmitReady.delete(form);
      var fallbackSubmitter = submitter || form.querySelector(
        'button[type="submit"], input[type="submit"]',
      );
      if (fallbackSubmitter && typeof fallbackSubmitter.click === 'function') {
        nativeSubmitReady.add(form);
        fallbackSubmitter.click();
      }
    }
    setTimeout(clearNativeSubmitReady, 0);
  }

  function dispatchSuccessfulConfirmations() {
    var markers = document.querySelectorAll(
      '[data-agl-form-success][data-form-kind]',
    );
    markers.forEach(function (marker) {
      var kind = marker.getAttribute('data-form-kind');
      var pending = readConfirmationKey(kind);
      if (!pending || pending.completed) return;

      var delivery = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ envelope: pending.envelope }),
        keepalive: true,
        credentials: 'same-origin',
      };
      var attempts = 0;
      function clearPending() {
        try { sessionStorage.removeItem(storageKey(kind)); } catch (_ignored) {}
      }
      function scheduleRetry() {
        if (attempts < MAX_RECONCILIATION_ATTEMPTS) {
          setTimeout(send, RECONCILIATION_DELAY_MS);
        }
      }
      function send() {
        attempts += 1;
        fetchWithTimeout(
          CONFIRM_ENDPOINT + '?idempotency_key=' + encodeURIComponent(pending.idempotencyKey),
          delivery,
        ).then(function (response) {
          if (response.status === 200 || response.status === 201) {
            clearPending();
          } else if (
            response.status === 202
          ) {
            scheduleRetry();
          } else {
            clearPending();
          }
        }).catch(scheduleRetry);
      }
      send();
    });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    var kind = FORMS[form.id];
    if (!kind) return;
    if (nativeSubmitReady.has(form)) {
      nativeSubmitReady.delete(form);
      return;
    }
    if (preparingForms.has(form)) {
      event.preventDefault();
      return;
    }
    clearStoredConfirmation(kind);
    event.preventDefault();
    preparingForms.add(form);
    var submitter = event.submitter;
    prepareWithinBudget(form, kind).catch(function () {
      // Turnstile/email confirmation is supplemental; preserve native Shopify submit.
    }).then(function () {
      preparingForms.delete(form);
      resumeNativeSubmit(form, submitter);
    });
  }, true);

  dispatchSuccessfulConfirmations();
})();
